import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Trash2, Package, ImageIcon, AlertTriangle } from 'lucide-react';

export default function Lagerverwaltung() {
  const { API } = useAuth();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', category: '', description: '', quantity: 0, unit: 'Stk.', location: '', min_stock: 0 });
  const [photo, setPhoto] = useState(null);
  const fileRef = useRef(null);

  const loadItems = async (q = '') => {
    const res = await axios.get(`${API}/inventory${q ? `?search=${encodeURIComponent(q)}` : ''}`);
    setItems(res.data);
  };

  useEffect(() => { loadItems(); }, [API]);

  useEffect(() => {
    const t = setTimeout(() => loadItems(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', category: '', description: '', quantity: 0, unit: 'Stk.', location: '', min_stock: 0 });
    setPhoto(null);
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ name: item.name, category: item.category || '', description: item.description || '', quantity: item.quantity, unit: item.unit || 'Stk.', location: item.location || '', min_stock: item.min_stock || 0 });
    setPhoto(null);
    setShowForm(true);
  };

  const handleSave = async () => {
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (photo) fd.append('file', photo);
    try {
      if (editing) {
        const res = await axios.put(`${API}/inventory/${editing.id}`, fd);
        setItems(prev => prev.map(i => i.id === editing.id ? res.data : i));
        toast.success('Aktualisiert');
      } else {
        const res = await axios.post(`${API}/inventory`, fd);
        setItems(prev => [...prev, res.data]);
        toast.success('Hinzugefügt');
      }
      setShowForm(false);
    } catch { toast.error('Fehler'); }
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/inventory/${id}`);
    setItems(prev => prev.filter(i => i.id !== id));
    toast.success('Gelöscht');
  };

  const categories = [...new Set(items.map(i => i.category).filter(Boolean))];

  return (
    <div className="space-y-4 animate-fade-in" data-testid="lager-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-white tracking-tight">Lagerverwaltung</h1>
        <Button onClick={openCreate} data-testid="add-inventory" className="bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm shadow-[0_0_15px_rgba(245,158,11,0.3)]">
          <Plus className="w-4 h-4 mr-2" />Neuer Bestand
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input data-testid="inventory-search" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Material, Werkzeug oder Möbel suchen..." className="pl-10 bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600" />
      </div>

      {categories.length > 0 && (
        <div className="flex gap-2 flex-wrap text-xs">
          <span className="text-slate-500 font-mono uppercase tracking-wider py-1">Kategorien:</span>
          {categories.map(c => (
            <button key={c} onClick={() => setSearch(c)}
              className="px-2 py-1 bg-slate-800 text-slate-300 hover:bg-slate-700 rounded-sm transition-colors">{c}</button>
          ))}
        </div>
      )}

      <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm overflow-hidden">
        <div className="grid grid-cols-[60px_1fr_120px_80px_80px_120px_100px] gap-3 px-4 py-3 bg-slate-900/80 text-xs font-mono uppercase text-slate-500 tracking-wider border-b border-slate-800/40">
          <span>Foto</span><span>Bezeichnung</span><span>Kategorie</span><span>Bestand</span><span>Min.</span><span>Lagerort</span><span></span>
        </div>
        {items.length ? items.map(item => {
          const lowStock = item.min_stock > 0 && item.quantity <= item.min_stock;
          return (
            <div key={item.id} className="grid grid-cols-[60px_1fr_120px_80px_80px_120px_100px] gap-3 px-4 py-3 border-b border-slate-800/40 hover:bg-slate-900/80 transition-colors items-center group"
              data-testid={`inv-${item.id}`}>
              <div className="w-10 h-10 rounded-sm bg-slate-800 border border-slate-700 overflow-hidden flex items-center justify-center">
                {item.has_photo ? (
                  <img src={`${API}/inventory/${item.id}/photo`} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Package className="w-4 h-4 text-slate-600" strokeWidth={1.5} />
                )}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-200 truncate">{item.name}</p>
                {item.description && <p className="text-xs text-slate-500 truncate">{item.description}</p>}
              </div>
              <Badge className="bg-slate-800 text-slate-300 border-slate-700 rounded-sm text-xs justify-center">{item.category || '—'}</Badge>
              <div className="flex items-center gap-1">
                {lowStock && <AlertTriangle className="w-3 h-3 text-amber-400" />}
                <span className={`text-sm font-mono ${lowStock ? 'text-amber-400' : 'text-slate-300'}`}>{item.quantity} {item.unit}</span>
              </div>
              <span className="text-sm font-mono text-slate-500">{item.min_stock || '—'}</span>
              <span className="text-xs text-slate-400 truncate">{item.location || '—'}</span>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button variant="ghost" size="icon" onClick={() => openEdit(item)} className="text-slate-400 hover:text-amber-400 w-8 h-8"><Pencil className="w-3.5 h-3.5" /></Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)} className="text-slate-400 hover:text-rose-400 w-8 h-8"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          );
        }) : <div className="p-8 text-center text-slate-500">{search ? 'Keine Ergebnisse' : 'Noch keine Bestände erfasst'}</div>}
      </div>

      <Dialog open={showForm} onOpenChange={v => { setShowForm(v); if (!v) setEditing(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200 max-w-lg">
          <DialogHeader><DialogTitle className="text-white">{editing ? 'Bestand bearbeiten' : 'Neuer Bestand'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-sm bg-slate-950 border border-slate-800 flex items-center justify-center overflow-hidden cursor-pointer"
                onClick={() => fileRef.current?.click()}>
                {photo ? (
                  <img src={URL.createObjectURL(photo)} alt="" className="w-full h-full object-cover" />
                ) : editing?.has_photo ? (
                  <img src={`${API}/inventory/${editing.id}/photo`} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center"><ImageIcon className="w-6 h-6 text-slate-600 mx-auto" /><span className="text-[9px] text-slate-600">Foto</span></div>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => setPhoto(e.target.files[0])} />
              <div className="flex-1 space-y-2">
                <div><Label className="text-slate-300 text-xs">Bezeichnung</Label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="z.B. Akkuschrauber Makita"
                  className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600" /></div>
                <div><Label className="text-slate-300 text-xs">Kategorie</Label>
                <Input value={form.category} onChange={e => setForm({...form, category: e.target.value})} placeholder="z.B. Werkzeug, Material, Möbel"
                  className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600" /></div>
              </div>
            </div>
            <div><Label className="text-slate-300 text-xs">Beschreibung</Label>
            <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Details, Seriennummer, etc."
              className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600" rows={2} /></div>
            <div className="grid grid-cols-4 gap-3">
              <div><Label className="text-slate-300 text-xs">Bestand</Label>
              <Input type="number" value={form.quantity} onChange={e => setForm({...form, quantity: parseFloat(e.target.value)||0})}
                className="bg-slate-950 border-slate-800 text-slate-200" /></div>
              <div><Label className="text-slate-300 text-xs">Einheit</Label>
              <Input value={form.unit} onChange={e => setForm({...form, unit: e.target.value})}
                className="bg-slate-950 border-slate-800 text-slate-200" /></div>
              <div><Label className="text-slate-300 text-xs">Min. Bestand</Label>
              <Input type="number" value={form.min_stock} onChange={e => setForm({...form, min_stock: parseFloat(e.target.value)||0})}
                className="bg-slate-950 border-slate-800 text-slate-200" /></div>
              <div><Label className="text-slate-300 text-xs">Lagerort</Label>
              <Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="Regal A2"
                className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600" /></div>
            </div>
            <Button onClick={handleSave} className="w-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm">
              {editing ? 'Speichern' : 'Bestand anlegen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
